"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { ArrowRight, Compass } from "lucide-react";

type Destination = { code: string; name: string; price?: string; slug?: string };

/**
 * Voyage Journal — travel-magazine hero per DESIGN_INSTRUCTIONS.md: a featured
 * destination "story" with large photography (warm wash placeholder) + an
 * editorial lead-in, plus a strip of quick destination entries. Client.
 */
export default function MagazineHero({ destinations }: { destinations: Destination[] }) {
  const { t } = useTranslations();
  const featured = destinations[0];
  const rest = destinations.slice(1, 5);
  const guideHref = (d?: Destination) => (d?.slug ? `/product/${d.slug}/` : "/destinations/");

  return (
    <section className="container mx-auto px-4 pt-8 pb-10">
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-stretch">
        {/* featured story */}
        <Link href={guideHref(featured)} onClick={() => featured && track({ event_name: "destination_hub_clicked", experiment_id: "wf_content_first_editorial_v1", items: [{ item_id: featured.code, item_name: featured.name, country: featured.name }] })} className="bm-card group relative block min-h-[340px] lg:min-h-[420px] bg-gradient-to-br from-amber-100/30 via-orange-50/20 to-yellow-100/30 flex items-end transition-all duration-300 hover:shadow-xl">
          <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" aria-hidden="true" />
          <div className="absolute left-0 right-0 bottom-0 p-6 sm:p-8 text-white">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wide backdrop-saturate-150">{t("bm.hero.badge", "Featured destination")}</span>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl text-white drop-shadow">{t("bm.hero.title", "Where to next?")}</h1>
            <p className="mt-2 text-white/90 max-w-lg drop-shadow text-sm sm:text-base">{t("bm.hero.sub", "Inspiring guides, smart itineraries, and tips on where to stay — plus how to stay connected on every trip.")}</p>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-foreground font-bold px-5 h-10 text-sm transition-all group-hover:gap-3 group-active:scale-[0.98]">{featured ? t("bm.hero.read", "Read the {name} guide").replace("{name}", featured.name) : t("bm.hero.explore", "Explore guides")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
          </div>
        </Link>

        {/* quick destinations */}
        <div className="grid grid-cols-2 gap-3">
          {rest.map((d) => (
            <Link key={d.code} href={guideHref(d)} onClick={() => track({ event_name: "destination_hub_clicked", experiment_id: "wf_content_first_editorial_v1", items: [{ item_id: d.code, item_name: d.name, country: d.name }] })} className="bm-card bm-photo bm-photo-scrim bm-photo-wash group relative block min-h-[160px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
              <img src={`https://flagcdn.com/${d.code.toLowerCase()}.svg`} alt="" role="presentation" width={28} height={20} className="absolute top-3 right-3 h-5 w-7 rounded shadow ring-1 ring-white/40" loading="lazy" referrerPolicy="no-referrer" />
              <div className="absolute left-0 right-0 bottom-0 p-3 text-white">
                <div className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-white/80"><Compass className="w-3 h-3" />{t("bm.hero.guide", "Guide")}</div>
                <div className="font-bold drop-shadow">{d.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
